import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { eligibleExperiencesQueryOptions, GUIDED_EXPERIENCE_QUERY_KEYS } from '@/api/queries/guided-experiences';
import * as guidedExperiencesApi from '@/api/endpoints/guided-experiences';
import * as userPreferencesApi from '@/api/endpoints/user-preferences';

export interface UseExistingUserPromptReturn {
  isVisible: boolean;
  dismiss: () => Promise<void>;
  save: (cuisines: string[], dietaryRestrictions: string[]) => Promise<void>;
}

const EXPERIENCE_ID = 'existing_user_taste_prompt';

export function useExistingUserPrompt(): UseExistingUserPromptReturn {
  const queryClient = useQueryClient();

  const { data: eligibilityData } = useQuery(
    eligibleExperiencesQueryOptions()
  );

  const isVisible = useMemo(() => {
    if (!eligibilityData) return false;
    return eligibilityData.experiences.some((e) => e.experience_id === EXPERIENCE_ID);
  }, [eligibilityData]);

  const dismiss = useCallback(async () => {
    await guidedExperiencesApi.startExperience(EXPERIENCE_ID);
    await guidedExperiencesApi.dismissExperience(EXPERIENCE_ID);
    queryClient.invalidateQueries({
      queryKey: GUIDED_EXPERIENCE_QUERY_KEYS.eligible(),
    });
  }, [queryClient]);

  const save = useCallback(async (cuisines: string[], dietaryRestrictions: string[]) => {
    await userPreferencesApi.updateUserPreferences({
      cuisine_preferences: cuisines,
      dietary_restrictions: dietaryRestrictions,
    });
    await guidedExperiencesApi.startExperience(EXPERIENCE_ID);
    await guidedExperiencesApi.completeExperience(EXPERIENCE_ID);
    queryClient.invalidateQueries({
      queryKey: GUIDED_EXPERIENCE_QUERY_KEYS.eligible(),
    });
  }, [queryClient]);

  return { isVisible, dismiss, save };
}
